import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';

// Admin access is granted by the "Admin" role carried in the user's JWT (set at login for
// accounts on the Admin:Emails list). The gate below checks that role via VerifySecret:
//   'checking'  – verifying the current token
//   'noauth'    – not signed in (or token expired) → prompt to sign in
//   'notadmin'  – signed in, but the account has no admin role
//   'ok'        – admin, render the panel
type GateStatus = 'checking' | 'noauth' | 'notadmin' | 'ok';

const CONSOLES = [
    { to: "/admin/statistiky", label: "Statistiky", ready: true },
    { to: "/admin/studenti", label: "Studenti", ready: true },
    { to: "/admin/vyuka", label: "Výuka", ready: true },
    { to: "/admin/skupiny", label: "Skupiny", ready: true },
    { to: "/admin/komunita", label: "Komunita", ready: true },
    { to: "/admin/online", label: "Online lekce", ready: true },
    { to: "/admin/fakulty", label: "Fakulty", ready: true },
    { to: "/admin/knihovna", label: "Knihovna", ready: true },
    { to: "/admin/obchod", label: "Obchod", ready: true },
    { to: "/admin/nastaveni", label: "Nastavení", ready: true },
    { to: "/admin/blog", label: "Blog", ready: true },
    { to: "/admin/logy", label: "Logy", ready: true },
];

export default function AdminLayout() {
    const [status, setStatus] = useState<GateStatus>('checking');
    const navigate = useNavigate();

    const api = new ApiHelper();

    async function verify() {
        // No token at all → straight to the sign-in prompt (don't call the API).
        if (!localStorage.getItem("token")) { setStatus('noauth'); return; }
        try {
            // Resolves only on 2xx, which (given [Authorize(Roles="Admin")] on the backend)
            // means the caller's token carries the Admin role.
            await api.adminApi.verifySecret();
            setStatus('ok');
        }
        catch (x: any) {
            // 401 = missing/expired token → sign in again; anything else (403) = valid
            // login without the admin role.
            if (x && x.status === 401) { localStorage.removeItem("token"); setStatus('noauth'); }
            else setStatus('notadmin');
        }
    }

    useEffect(() => {
        verify();
        document.body.classList.add("adminMode");
        // A mid-session admin 401/403 (fired by ApiHelper.adminError) → re-run the gate.
        const onUnauth = () => verify();
        window.addEventListener("admin-unauthorized", onUnauth);
        return () => {
            document.body.classList.remove("adminMode");
            window.removeEventListener("admin-unauthorized", onUnauth);
        };
    }, []);

    function signOut() { localStorage.removeItem("token"); setStatus('noauth'); }

    if (status === 'checking') return <div className="adminGate" />;

    if (status === 'noauth') return (
        <div className="adminGate">
            <div className="gateCard">
                <h1>Administrace</h1>
                <p>Pro přístup do správy se přihlas administrátorským účtem.</p>
                <Link className="gateButton" to="/prihlaseni" state={{ from: '/admin' }}>Přihlásit se</Link>
            </div>
        </div>
    );

    if (status === 'notadmin') return (
        <div className="adminGate">
            <div className="gateCard">
                <h1>Administrace</h1>
                <p>Tento účet nemá oprávnění pro přístup do správy.</p>
                <button onClick={() => navigate('/kurz')}>Zpět do aplikace</button>
                <span className="gateLink" onClick={signOut}>Přihlásit se jiným účtem</span>
            </div>
        </div>
    );

    return (
        <div className="admin">
            <aside className="adminNav">
                <div className="brand">MyMed <span>admin</span></div>
                <nav>
                    {CONSOLES.map(c => c.ready
                        ? <NavLink key={c.to} to={c.to} className={({ isActive }) => "navItem" + (isActive ? " active" : "")}>{c.label}</NavLink>
                        : <span key={c.to} className="navItem soon">{c.label}<i>brzy</i></span>)}
                </nav>
                <div className="navFoot">
                    <a href="/kurz">← Zpět do aplikace</a>
                    <span onClick={signOut}>Odhlásit se</span>
                </div>
            </aside>
            <main className="adminMain"><Outlet /></main>
        </div>
    );
}
